import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/api';

export type Theme = 'light' | 'dark' | 'system';
export type ApiTheme = 'light' | 'dark' | 'auto';
export type PrimaryColor = 'blue' | 'teal' | 'green' | 'orange' | 'pink' | 'purple';

/** Map client theme (`system`) to API/Firestore preference value (`auto`). */
export function themeToApiPreference(theme: Theme): ApiTheme {
  return theme === 'system' ? 'auto' : theme;
}

/** Map API/Firestore preference value to client theme store value. */
export function themeFromApiPreference(value: unknown): Theme {
  if (value === 'light' || value === 'dark') return value;
  return 'system';
}

export const PRIMARY_COLOR_TOKENS: Record<PrimaryColor, { primary: string; primaryForeground: string }> = {
  blue: { primary: '210 100% 50%', primaryForeground: '210 40% 98%' },
  teal: { primary: '175 84% 40%', primaryForeground: '210 40% 98%' },
  green: { primary: '142 71% 45%', primaryForeground: '210 40% 98%' },
  orange: { primary: '24 95% 53%', primaryForeground: '210 40% 98%' },
  pink: { primary: '330 81% 60%', primaryForeground: '210 40% 98%' },
  purple: { primary: '262 83% 58%', primaryForeground: '210 40% 98%' },
};

export const PRIMARY_COLOR_OPTIONS: ReadonlyArray<{ id: PrimaryColor; labelKey: string }> = [
  { id: 'blue', labelKey: 'account.primaryColor.colors.blue' },
  { id: 'teal', labelKey: 'account.primaryColor.colors.teal' },
  { id: 'green', labelKey: 'account.primaryColor.colors.green' },
  { id: 'orange', labelKey: 'account.primaryColor.colors.orange' },
  { id: 'pink', labelKey: 'account.primaryColor.colors.pink' },
  { id: 'purple', labelKey: 'account.primaryColor.colors.purple' },
];

export const DEFAULT_PRIMARY_COLOR: PrimaryColor = 'blue';

export function normalizePrimaryColor(value: unknown): PrimaryColor {
  if (typeof value === 'string' && value in PRIMARY_COLOR_TOKENS) {
    return value as PrimaryColor;
  }
  return DEFAULT_PRIMARY_COLOR;
}

interface ThemeState {
  theme: Theme;
  primaryColor: PrimaryColor;
  setTheme: (theme: Theme, syncWithBackend?: boolean) => void;
  setPrimaryColor: (primaryColor: PrimaryColor, syncWithBackend?: boolean) => void;
  getEffectiveTheme: () => 'light' | 'dark';
  syncTheme: (theme: Theme) => Promise<void>;
  syncPrimaryColor: (primaryColor: PrimaryColor) => Promise<void>;
}

/**
 * Theme store
 * Manages application theme with system preference detection
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      primaryColor: DEFAULT_PRIMARY_COLOR,
      setTheme: (theme, syncWithBackend = false) => {
        set({ theme });
        if (syncWithBackend) {
          get().syncTheme(theme);
        }
      },
      setPrimaryColor: (primaryColor, syncWithBackend = false) => {
        const normalized = normalizePrimaryColor(primaryColor);
        set({ primaryColor: normalized });
        if (syncWithBackend) {
          get().syncPrimaryColor(normalized);
        }
      },
      getEffectiveTheme: () => {
        const { theme } = get();
        if (theme === 'system') {
          return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return theme;
      },
      syncTheme: async (theme) => {
        try {
          await api.put('/preference', { theme: themeToApiPreference(theme) });
        } catch (error) {
          console.error('Failed to sync theme preference:', error);
        }
      },
      syncPrimaryColor: async (primaryColor) => {
        try {
          await api.put('/preference', { primaryColor: normalizePrimaryColor(primaryColor) });
        } catch (error) {
          console.error('Failed to sync primary color preference:', error);
        }
      },
    }),
    {
      name: 'trove-theme-storage',
      partialize: (state) => ({
        theme: state.theme,
        primaryColor: state.primaryColor,
      }),
    }
  )
);
